import { createId } from './id';
import { median, percentile } from './stats';
import type { EnvironmentSnapshot, SessionRecord } from './types';

export interface FrameDiagnostics {
  sampleCount: number;
  medianIntervalMs: number;
  p95IntervalMs: number;
  estimatedRefreshRateHz: number;
  droppedFrameRatio: number;
  stable: boolean;
}

export function analyzeFrameIntervals(intervals: readonly number[]): FrameDiagnostics {
  const valid = intervals.filter((interval) => Number.isFinite(interval) && interval > 0);
  const medianIntervalMs = median(valid) ?? 0;
  const p95IntervalMs = percentile(valid, 0.95) ?? 0;
  const dropThreshold = medianIntervalMs * 2.5;
  const droppedFrameRatio = valid.length === 0
    ? 1
    : valid.filter((interval) => interval > dropThreshold).length / valid.length;
  return {
    sampleCount: valid.length,
    medianIntervalMs,
    p95IntervalMs,
    estimatedRefreshRateHz:
      medianIntervalMs === 0 ? 0 : Number((1000 / medianIntervalMs).toFixed(3)),
    droppedFrameRatio,
    stable:
      valid.length >= 30 &&
      droppedFrameRatio <= 0.05 &&
      p95IntervalMs <= medianIntervalMs * 1.75,
  };
}

export function isFrameDrop(intervalMs: number, expectedIntervalMs: number): boolean {
  return intervalMs > expectedIntervalMs * 2.5;
}

export interface FrameSamplerOptions {
  sampleCount?: number;
  requestFrame?: (callback: FrameRequestCallback) => number;
  cancelFrame?: (handle: number) => void;
}

export function sampleFrameDiagnostics(options: FrameSamplerOptions = {}): Promise<FrameDiagnostics> {
  const sampleCount = Math.max(30, Math.round(options.sampleCount ?? 90));
  const requestFrame = options.requestFrame ?? globalThis.requestAnimationFrame?.bind(globalThis);
  const cancelFrame = options.cancelFrame ?? globalThis.cancelAnimationFrame?.bind(globalThis);
  if (!requestFrame) {
    return Promise.reject(new Error('requestAnimationFrame is unavailable'));
  }

  return new Promise((resolve) => {
    const intervals: number[] = [];
    let previous: number | undefined;
    let handle = 0;
    const onFrame: FrameRequestCallback = (timestamp) => {
      if (previous !== undefined) intervals.push(timestamp - previous);
      previous = timestamp;
      if (intervals.length >= sampleCount) {
        if (cancelFrame && handle) cancelFrame(handle);
        resolve(analyzeFrameIntervals(intervals));
        return;
      }
      handle = requestFrame(onFrame);
    };
    handle = requestFrame(onFrame);
  });
}

export interface EnvironmentCaptureOptions {
  calibrationId?: string;
  capturedAt?: string;
  id?: string;
  conditionId?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  devicePixelRatio?: number;
  userAgent?: string;
}

export function createEnvironmentSnapshot(
  diagnostics: FrameDiagnostics,
  options: EnvironmentCaptureOptions = {},
): EnvironmentSnapshot {
  const browserWindow = typeof window === 'undefined' ? undefined : window;
  const browserNavigator = typeof navigator === 'undefined' ? undefined : navigator;
  const id = options.id ?? createId('env');
  return {
    id,
    conditionId: options.conditionId ?? id,
    capturedAt: options.capturedAt ?? new Date().toISOString(),
    viewportWidth: options.viewportWidth ?? browserWindow?.innerWidth ?? 0,
    viewportHeight: options.viewportHeight ?? browserWindow?.innerHeight ?? 0,
    devicePixelRatio: options.devicePixelRatio ?? browserWindow?.devicePixelRatio ?? 1,
    refreshRateHz: diagnostics.estimatedRefreshRateHz,
    frameIntervalMedianMs: diagnostics.medianIntervalMs,
    frameIntervalP95Ms: diagnostics.p95IntervalMs,
    userAgent: options.userAgent ?? browserNavigator?.userAgent ?? 'unknown',
    inputMethod: 'pointer',
    calibrationId: options.calibrationId,
  };
}

export function getEnvironmentConditionId(environment: EnvironmentSnapshot): string {
  return environment.conditionId ?? environment.id;
}

export function getSessionEnvironmentConditionId(session: SessionRecord): string {
  return session.environmentConditionId ?? session.environmentId;
}

export async function measureEnvironment(
  options: EnvironmentCaptureOptions & FrameSamplerOptions = {},
): Promise<{ environment: EnvironmentSnapshot; diagnostics: FrameDiagnostics }> {
  const diagnostics = await sampleFrameDiagnostics(options);
  return { environment: createEnvironmentSnapshot(diagnostics, options), diagnostics };
}
